import { apiClient } from './api';

/**
 * The Context type enum, mirroring the backend (revenant-api
 * `src/models/Context.js`) and the API's route validators.
 *
 * SINGLE SOURCE OF TRUTH — add new values here only. The union used to be
 * hand-copied across ~9 sites, and a value missing from one of them is exactly
 * what produced REV-494 and then REV-516.
 */
export const CONTEXT_TYPE_VALUES = ['repository', 'deployment', 'website', 'organization'] as const;
export type ContextType = (typeof CONTEXT_TYPE_VALUES)[number];

// Types
export interface ContextSummary {
  totalScans: number;
  openFindings: number;
  /** ISO timestamp of the most recent COMPLETED scan, or null if never. */
  lastScanAt: string | null;
}

export interface Context {
  id: string;
  organization_id: string;
  /**
   * REV-497: human-readable URL segment, unique within the organization.
   * Optional so a client running against an API that predates the column still
   * type-checks; call sites fall back to `id`.
   */
  slug?: string;
  name: string;
  context_type: ContextType;
  canonical_identifier: string;
  status: 'active' | 'paused' | 'removed';
  /** null when never set or cleared (REV-956, REV-975). */
  description?: string | null;
  /**
   * REV-611: per-place activity, present only when the caller asked for it via
   * `include_summary`. Always fully populated when present — a never-scanned
   * place reports zeros and a null date rather than an absent summary, so the
   * card never has to guess whether "no data" means "never scanned" or "not
   * fetched".
   */
  summary?: ContextSummary;
  settings?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  /**
   * REV-810: the origins this tracked place accepts evidence for, beyond the
   * origin of its own identifier. null or absent means none were added.
   */
  allowed_origins?: string[] | null;
  /**
   * REV-975: camelCase. Every context route serialises the Sequelize instance,
   * and an `underscored` model still emits its timestamps under their
   * attribute names; only the database columns are snake_case. Checked against
   * the captured responses in contextService.contract.test.ts.
   */
  createdAt: string;
  updatedAt: string;
}

export interface ContextSuggestion {
  id: string;
  canonical_identifier: string;
  context_type: ContextType;
  source: 'ci_upload' | 'cli_upload' | 'dashboard_view' | 'api_upload';
  status: 'pending' | 'registered' | 'dismissed';
  detection_count: number;
  first_detected_at: string;
  last_detected_at: string;
}

export interface CreateContextData {
  name: string;
  context_type: ContextType;
  canonical_identifier: string;
  description?: string;
  settings?: Record<string, unknown>;
  /** REV-810: extra origins this place accepts evidence for. */
  allowed_origins?: string[] | null;
}

export interface UpdateContextData {
  name?: string;
  /** null clears it (REV-956). */
  description?: string | null;
  settings?: Record<string, unknown>;
  /** REV-502: editable so a mis-registered tracked place can be corrected
   *  without deleting it (and losing its finding history). */
  context_type?: ContextType;
  canonical_identifier?: string;
  /** REV-810: null clears the list back to the place's own origin only. */
  allowed_origins?: string[] | null;
}

export interface ContextStats {
  totalScans: number;
  totalFindings: number;
  findingsBySeverity: {
    critical: number;
    major: number;
    minor: number;
    info: number;
  };
  avgDurationMs: number;
  lastScanAt: string | null;
  lastScanSource: string | null;
  scansBySource: Record<string, number>;
}

export interface ContextUsage {
  used: number;
  limit: number | null;
  plan: string;
}

// Historical standalone-scan import (REV-249/REV-411). A new/migrating customer
// uploads accumulated local scan results so regression and lifecycle tracking
// have a baseline from day one.
export interface ImportScanInput {
  url: string;
  source?: string;
  scannedAt?: string;
  commitSha?: string;
  branch?: string;
  prNumber?: number;
  externalId?: string;
  findings?: unknown[];
}

export interface ImportScanHistoryResult {
  contextId: string;
  scansSubmitted: number;
  scansImported: number;
  scansFailed: number;
  totalFindings: number;
  overLimit: boolean;
  results: Array<{ index: number; url?: string; scanRunId?: string; findings?: number }>;
  errors: Array<{ index: number; url?: string; error: string }>;
}

// Bulk context registration (REV-250). A large organization or a team
// migrating off another tool registers many Tracked Places at once (parsed
// from a CSV/JSON file client-side) instead of one at a time.
export interface BulkRegisterContextInput {
  canonical_identifier: string;
  name?: string;
  context_type?: ContextType;
  description?: string;
}

export type BulkRegisterRowStatus =
  | 'created'
  | 'already_exists'
  | 'duplicate_in_batch'
  | 'over_limit';

export interface BulkRegisterResultRow {
  index: number;
  name: string;
  canonical_identifier: string;
  context_type: ContextType;
  contextId?: string;
  status: BulkRegisterRowStatus;
}

export interface BulkRegisterResult {
  submitted: number;
  created: number;
  skipped: number;
  failed: number;
  overLimit: boolean;
  usage: { used: number; limit: number | null; plan: string };
  results: BulkRegisterResultRow[];
  errors: Array<{ index: number; name?: string; error: string }>;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  requestId?: string;
}

interface ContextListResponse {
  success: boolean;
  data: {
    contexts: Context[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  requestId?: string;
}

interface SuggestionListResponse {
  success: boolean;
  data: {
    suggestions: ContextSuggestion[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  requestId?: string;
}

interface ListContextsParams {
  [key: string]: unknown;
  /**
   * REV-611: ask the server for per-place scan and finding counts. Opt-in
   * because it costs two extra aggregates server-side; callers that only need
   * names should not pay for them.
   */
  include_summary?: boolean;
  limit?: number;
  offset?: number;
  status?: 'active' | 'paused' | 'removed';
  context_type?: ContextType;
  /**
   * REV-532: case-insensitive substring match on name or canonical_identifier.
   * The endpoint caps limit at 100, so a picker that pre-loads can only ever
   * show the first 100 places; searching server-side is what lets it scale.
   */
  search?: string;
}

interface ListSuggestionsParams {
  [key: string]: unknown;
  limit?: number;
  offset?: number;
  status?: 'pending' | 'registered' | 'dismissed';
}

// Context API
export const contextService = {
  // List contexts in organization
  list: async (orgId: string, params?: ListContextsParams) => {
    const response = await apiClient.get<ContextListResponse>(
      `/v1/organizations/${orgId}/contexts`,
      params
    );
    // Map to consistent format expected by UI components
    return {
      items: response.data.contexts,
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      totalPages: response.data.totalPages,
    };
  },

  // Get context by ID
  getById: async (orgId: string, contextId: string) => {
    const response = await apiClient.get<ApiResponse<Context>>(
      `/v1/organizations/${orgId}/contexts/${contextId}`
    );
    return response.data;
  },

  // Create context
  create: async (orgId: string, data: CreateContextData) => {
    const response = await apiClient.post<ApiResponse<Context>>(
      `/v1/organizations/${orgId}/contexts`,
      data
    );
    return response.data;
  },

  // Update context
  update: async (orgId: string, contextId: string, data: UpdateContextData) => {
    const response = await apiClient.patch<ApiResponse<Context>>(
      `/v1/organizations/${orgId}/contexts/${contextId}`,
      data
    );
    return response.data;
  },

  // Delete context
  delete: async (orgId: string, contextId: string) => {
    await apiClient.delete(`/v1/organizations/${orgId}/contexts/${contextId}`);
  },

  // Pause context
  pause: async (orgId: string, contextId: string) => {
    const response = await apiClient.post<ApiResponse<Context>>(
      `/v1/organizations/${orgId}/contexts/${contextId}/pause`
    );
    return response.data;
  },

  // Activate context
  activate: async (orgId: string, contextId: string) => {
    const response = await apiClient.post<ApiResponse<Context>>(
      `/v1/organizations/${orgId}/contexts/${contextId}/activate`
    );
    return response.data;
  },

  // Get context usage for organization
  getUsage: async (orgId: string) => {
    const response = await apiClient.get<ApiResponse<ContextUsage>>(
      `/v1/organizations/${orgId}/contexts/usage`
    );
    return response.data;
  },

  // Get test statistics for a context
  getStats: async (orgId: string, contextId: string) => {
    const response = await apiClient.get<ApiResponse<ContextStats>>(
      `/v1/organizations/${orgId}/contexts/${contextId}/stats`
    );
    return response.data;
  },

  // Get pending suggestion count
  getSuggestionCount: async (orgId: string) => {
    const response = await apiClient.get<ApiResponse<{ count: number }>>(
      `/v1/organizations/${orgId}/suggestions/count`
    );
    return response.data.count;
  },

  // List suggestions
  listSuggestions: async (orgId: string, params?: ListSuggestionsParams) => {
    const response = await apiClient.get<SuggestionListResponse>(
      `/v1/organizations/${orgId}/suggestions`,
      params
    );
    // Map to consistent format expected by UI components
    return {
      items: response.data.suggestions,
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      totalPages: response.data.totalPages,
    };
  },

  // Dismiss suggestion
  dismissSuggestion: async (orgId: string, suggestionId: string) => {
    const response = await apiClient.post<ApiResponse<ContextSuggestion>>(
      `/v1/organizations/${orgId}/suggestions/${suggestionId}/dismiss`
    );
    return response.data;
  },

  // Undismiss (restore) a suggestion
  undismissSuggestion: async (orgId: string, suggestionId: string) => {
    const response = await apiClient.post<ApiResponse<ContextSuggestion>>(
      `/v1/organizations/${orgId}/suggestions/${suggestionId}/undismiss`
    );
    return response.data;
  },

  // Register suggestion as context
  registerSuggestion: async (orgId: string, suggestionId: string, data?: { name?: string }) => {
    const response = await apiClient.post<ApiResponse<Context>>(
      `/v1/organizations/${orgId}/suggestions/${suggestionId}/register`,
      data
    );
    return response.data;
  },

  // Import historical standalone scan results onto a context (REV-249/REV-411)
  importScanHistory: async (orgId: string, contextId: string, scans: ImportScanInput[]) => {
    const response = await apiClient.post<ApiResponse<ImportScanHistoryResult>>(
      `/v1/organizations/${orgId}/contexts/${contextId}/import-scan-history`,
      { scans }
    );
    return response.data;
  },

  // Bulk-register multiple contexts in one request (REV-250)
  bulkRegister: async (orgId: string, contexts: BulkRegisterContextInput[]) => {
    const response = await apiClient.post<ApiResponse<BulkRegisterResult>>(
      `/v1/organizations/${orgId}/contexts/bulk`,
      { contexts }
    );
    return response.data;
  },
};

export default contextService;
